import { useEffect, useId, useState, type ComponentProps, type ReactNode } from 'react';
import AssetFilters from '../components/AssetFilters';
import {
  NO_ASSET_FILTER,
  assetFilterOptions,
  assetMatchesFilter,
  reconcileAssetFilter,
  type AssetFacets,
  type AssetFilter,
} from '../components/assetFilterTiers';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import { LINE_TYPE_LABELS, type LineType, type MarkingKind } from '../types';
import { getInstrumentDisplayName } from '../utils/instrumentTypes';
import WorkspaceTargetRail, { matchesRailQuery } from './WorkspaceTargetRail';
import { TABS, TAB_KINDS, assetRailTabRows, type AssetRailTab, type RailAssets } from './assetRailTabs';


/** One tab's list with its Type and Subtype filters. The rail remounts it on a tab change, so a
 *  new tab starts with both filters clear. With Needs review on, it lists only the pending rows;
 *  with Needs fix on, only the rows with a finding. */
function AssetTabList({ kind, items, onShownRowsChange, elementTypeLabel, needsReview, needsFix, ...railProps }:
  Omit<ComponentProps<typeof WorkspaceTargetRail>, 'tools' | 'needsReview' | 'needsFix'> & {
    kind: MarkingKind; onShownRowsChange?: (ids: string[]) => void; elementTypeLabel: (elementType: string) => string;
    needsReview?: { checked: boolean; onChange: (next: boolean) => void };
    needsFix: { checked: boolean; onChange: (next: boolean) => void };
  }) {
  const [filter, setFilter] = useState<AssetFilter>({ ...NO_ASSET_FILTER, kind });
  const isPending = (id: string) => Boolean(railProps.pendingIds?.has(id));
  const listedItems = items
    .filter((item) => !needsReview?.checked || isPending(item.id))
    .filter((item) => !needsFix.checked || item.needsFix);
  const facets: AssetFacets[] = listedItems.map((item) => ({ kind, element_type: item.element_type, subtype: item.subtype }));
  // An edit can take the chosen type off the last row that had it; the selects then show All.
  const shownFilter: AssetFilter = { ...reconcileAssetFilter(filter, facets), kind };
  const shownItems = listedItems.filter((_, index) => assetMatchesFilter(facets[index], shownFilter));
  const shownIdsKey = shownItems.map((item) => item.id).join('\n');
  useEffect(() => onShownRowsChange?.(shownIdsKey ? shownIdsKey.split('\n') : []), [onShownRowsChange, shownIdsKey]);
  // The rows Needs review lists on this tab under the current Type, Subtype and search, on or off.
  const needsReviewCount = shownItems
    .filter((item) => isPending(item.id) && matchesRailQuery(item, railProps.query ?? '')).length;
  // Counted the same way: the rows Needs fix lists under the current Type, Subtype and search.
  const needsFixCount = shownItems
    .filter((item) => item.needsFix && matchesRailQuery(item, railProps.query ?? '')).length;
  return (
    <WorkspaceTargetRail {...railProps}
      items={shownItems}
      needsReview={needsReview && { ...needsReview, count: needsReviewCount }}
      needsFix={{ ...needsFix, count: needsFixCount }}
      tools={
        <AssetFilters layout="stack" kindTier="fixed" elementTypeLabel={elementTypeLabel}
          filter={shownFilter} options={assetFilterOptions(facets, shownFilter)} onChange={setFilter} />
      }
    />
  );
}

/** The rail's Needs review filter and the drawing's pending agent proposals it marks. */
export interface AssetRailNeedsReview {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The ids an agent proposal waits on, per kind. Their rows wear the pending marker, on and off. */
  pendingIds: Record<MarkingKind, ReadonlySet<string>>;
  /** A pending row's click: open the review of that row. */
  onReview: (kind: MarkingKind, id: string) => void;
  /** What a tab shows with the filter on and nothing pending on the drawing. */
  emptyMessage?: ReactNode;
  /** The ids the current tab shows after its Type and Subtype, in row order, each time they change.
   *  The workspace's advance after a Save walks them. */
  onShownRowsChange?: (ids: string[]) => void;
}

/** The workspace's one asset rail: a tab per kind over the list of that kind on the drawing.
 *  With Needs review on, each tab lists only its pending rows; with Needs fix on, only the rows
 *  the drawing's validation holds a finding on. */
export default function WorkspaceAssetRail({
  tab, onTabChange, assets, activeEquipmentId, selectedInstrumentId, activeNodeId,
  onCenterEquipment, onEditEquipment, onCenterInstrument, onEditInstrument, onCenterNode, onEditNode,
  needsReview, query, onQueryChange, publishableInstrumentIds, publishableInstrumentsError,
}: {
  tab: AssetRailTab;
  onTabChange: (tab: AssetRailTab) => void;
  assets?: RailAssets;
  activeEquipmentId: string | null;
  selectedInstrumentId: string | null;
  activeNodeId: string | null;
  onCenterEquipment: (id: string) => void;
  onEditEquipment: (id: string) => void;
  onCenterInstrument: (id: string) => void;
  onEditInstrument: (id: string) => void;
  onCenterNode: (id: string) => void;
  onEditNode: (id: string) => void;
  needsReview?: AssetRailNeedsReview;
  /** The search box. Its owner sits above the list's remount (key={tab}), so a tab change keeps it. */
  query: string;
  onQueryChange: (next: string) => void;
  /** The instruments the Instruments tab lists. Omitted, it lists every instrument. */
  publishableInstrumentIds?: ReadonlySet<string>;
  /** Set when the publishable list failed to load: the Instruments tab says it lists every
   *  instrument, and offers a retry. */
  publishableInstrumentsError?: { onRetry: () => void };
}) {
  const tabId = useId();
  const center = { equipment: onCenterEquipment, instruments: onCenterInstrument, lines: onCenterNode }[tab];
  const edit = { equipment: onEditEquipment, instruments: onEditInstrument, lines: onEditNode }[tab];
  const { labelFor: equipmentTypeLabel } = useEquipmentTypes();
  const { items, groups } = assetRailTabRows(tab, assets, equipmentTypeLabel, publishableInstrumentIds);
  const kind = TAB_KINDS[tab];
  const pendingIds = needsReview?.pendingIds[kind];
  const drawingHasPending = Boolean(needsReview && Object.values(needsReview.pendingIds).some((ids) => ids.size > 0));
  // Held above the tab's remount, so the filter stays on across the tabs.
  const [needsFixOn, setNeedsFixOn] = useState(false);
  return (
    <AssetTabList key={tab} kind={kind}
      elementTypeLabel={{
        equipment: equipmentTypeLabel,
        instrument: getInstrumentDisplayName,
        node: (lineType: string) => LINE_TYPE_LABELS[lineType as LineType] ?? lineType,
      }[kind]}
      title={TABS.find((candidate) => candidate.value === tab)!.label}
      panelId={`${tabId}-panel`} labelledBy={`${tabId}-${tab}`}
      keepEmpty groups={groups}
      items={items}
      emptyMessage={needsReview?.checked && !drawingHasPending ? needsReview.emptyMessage : undefined}
      activeId={{ equipment: activeEquipmentId, instruments: selectedInstrumentId, lines: activeNodeId }[tab]}
      // A line row centers the line, as the Lines rail did; the other rows open their editor.
      onSelect={tab === 'lines' ? center : edit}
      onCenter={center}
      onEdit={(id) => { center(id); edit(id); }}
      needsReview={needsReview && { checked: needsReview.checked, onChange: needsReview.onChange }}
      needsFix={{ checked: needsFixOn, onChange: setNeedsFixOn }}
      pendingIds={pendingIds}
      onReview={needsReview && ((id) => needsReview.onReview(kind, id))}
      onShownRowsChange={needsReview?.onShownRowsChange}
      query={query} onQueryChange={onQueryChange}
      header={<>
        <div className="pnid-target-tabs" role="tablist" aria-label="Review assets">
          {TABS.map(({ value, label }, index) => (
            <button key={value} id={`${tabId}-${value}`} type="button" role="tab"
              aria-selected={tab === value} aria-controls={`${tabId}-panel`} tabIndex={tab === value ? 0 : -1}
              onClick={() => onTabChange(value)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                event.stopPropagation();
                const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? TABS.length - 1
                  : (index + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length;
                const next = TABS[nextIndex].value;
                onTabChange(next);
                requestAnimationFrame(() => document.getElementById(`${tabId}-${next}`)?.focus());
              }}>
              {label}
            </button>
          ))}
        </div>
        {tab === 'instruments' && publishableInstrumentsError && (
          <p className="pnid-target-notice" role="alert">
            Publishable list unavailable. Showing every instrument.{' '}
            <button type="button" onClick={publishableInstrumentsError.onRetry}>Retry</button>
          </p>
        )}
      </>}
    />
  );
}
